import type { BridgeContext } from '../types';
import type { TBridgeMethod, TBridgeMiddleware } from '../methods/types';

/**
 * The subset of BridgeContext fields that an embedded macro iframe is allowed
 * to override. These are identity/routing fields only — services, bridgeConfig,
 * and bridgeControls always remain from the host page's BridgeContext.
 */
export type EmbeddedMacroContext = Required<
	Pick<
		BridgeContext,
		| 'contextIds'
		| 'extension'
		| 'extensionDetails'
		| 'extensionPayload'
		| 'page'
		| 'resolverContext'
		| 'viewContext'
	>
>;

type EmbeddedMacroPayload = {
	embeddedMacroContext: EmbeddedMacroContext;
	payload: unknown;
};

function isEmbeddedMacroPayload(value: unknown): value is EmbeddedMacroPayload {
	return typeof value === 'object' && value !== null && 'embeddedMacroContext' in value;
}

function createOpenModalPayload<Payload>(
	payload: unknown,
	embeddedMacroContext: EmbeddedMacroContext,
): Payload {
	const { extension, extensionPayload, resolverContext } = embeddedMacroContext;

	return {
		...(payload as object),
		embeddedMacroLocalId: resolverContext?.localId,
		extension,
		extensionData: resolverContext?.extension,
		extensionPayload,
		isEmbeddedMacroModal: true,
	} as Payload;
}

export const withEmbeddedMacroContext: TBridgeMiddleware =
	<Payload, Result>(
		method: TBridgeMethod<BridgeContext, Payload, Result>,
		methodName: string,
	): TBridgeMethod<BridgeContext, Payload, Result> =>
	async (context, embeddedMacroPayload) => {
		if (!isEmbeddedMacroPayload(embeddedMacroPayload)) {
			return method(context, embeddedMacroPayload);
		}
		const { embeddedMacroContext, payload } = embeddedMacroPayload;
		const {
			contextIds,
			extension,
			extensionDetails,
			extensionPayload,
			page,
			resolverContext,
			viewContext,
		} = embeddedMacroContext;

		const mergedContext: BridgeContext = {
			...context,
			contextIds,
			extension,
			extensionDetails,
			extensionPayload,
			page,
			resolverContext,
			viewContext,
		};

		if (methodName === 'openModal') {
			return method(mergedContext, createOpenModalPayload(payload, embeddedMacroContext));
		}
		// `payload` is `unknown` from the embedded macro payload — cast to Payload since this middleware
		// intentionally unwraps it and forwards the inner args as-is to the method.
		return method(mergedContext, payload as Payload);
	};
